import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { LitElement, css, html, nothing } from "lit";
import { property, query, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import "@progressive-development/pd-forms/pd-form-container";
//#region src/pd-default-wizard-step/PdDefaultWizardStep.ts
/**
* PdDefaultWizardStep - Abstract base class for wizard steps
*
* Provides a consistent pattern for wizard step implementations with:
* - Form validation integration (pd-form-container)
* - Step title display
* - Data extraction and validation
* - Optional auto-save via wizard-step-update event
*
* @example
* ```typescript
* interface OrderFormData {
*   name: string;
*   email: string;
* }
*
* interface ContactStepData {
*   contactName: string;
*   contactEmail: string;
* }
*
* @customElement("my-contact-step")
* export class MyContactStep extends PdDefaultWizardStep<OrderFormData, ContactStepData> {
*   protected _renderFormContent() {
*     return html`
*       <pd-input label="Name" valueName="contactName" required></pd-input>
*       <pd-input label="Email" valueName="contactEmail" required></pd-input>
*     `;
*   }
* }
* ```
*
* @fires wizard-step-update - Fired when form data changes (for auto-save)
*/
/** Delay before firing wizard-step-update event (debounce) */
var DELAY_TIME = 3e3;
/**
* Abstract base class for wizard steps.
*
* Contains generics for the OrderFormData (T) and the specific FormData (F) for this Step.
*
* @typeParam T - The complete order/wizard form data type
* @typeParam F - The form data type specific to this step
*/
var PdDefaultWizardStep = class extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.stepTitle = "Step 1";
		this.stepDescription = "";
		this.withRequiredInfo = false;
		this.withAutoTrim = true;
		this._errorMsg = "";
		this._validForm = false;
	}
	static {
		this.styles = [css`
      h3 {
        color: var(--pd-default-font-title-col);
        font-family: var(--pd-default-font-title-family);
      }
      .step-description {
        color: var(--pd-default-font-content-col, var(--pd-default-font-col));
        font-size: 0.95em;
        line-height: 1.5;
        margin-bottom: 1rem;
      }
    `];
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		if (this._inputChangeTimeoutRef) {
			window.clearTimeout(this._inputChangeTimeoutRef);
			this._inputChangeTimeoutRef = void 0;
		}
	}
	render() {
		return html`
      <h3>${this._getTitle()}</h3>
      ${this.stepDescription ? html`<div class="step-description">
              ${unsafeHTML(this.stepDescription)}
            </div>` : nothing}

      <pd-form-container
        id="stepFormContainerId"
        class="form-container"
        commonError="${this._errorMsg}"
        ?autoTrimm="${this.withAutoTrim}"
        ?requiredFieldInfo="${this.withRequiredInfo}"
      >
        ${this._renderFormContent()}
      </pd-form-container>
    `;
	}
	/** Whether the step form is valid */
	get valid() {
		return this._formElement?.valid && this._isValidFormData();
	}
	/**
	* Whether the user changed at least one field of this step.
	* Returns `undefined` when the underlying form element does not support
	* dirty tracking — the wizard's dirty guard then ignores this step
	* instead of treating it as "unchanged".
	*/
	get dirty() {
		const dirty = this._formElement?.dirty;
		return typeof dirty === "boolean" ? dirty : void 0;
	}
	/** Trigger form validation */
	async triggerValidate() {
		return this._formElement?.triggerValidate();
	}
	/**
	* Extract and merge form data into the order
	* @param currentOrder - Current order data
	* @returns Updated order with this step's data
	*/
	setFormData(currentOrder) {
		this._errorMsg = "";
		try {
			return {
				...currentOrder,
				...this._prepareDataFromForm()
			};
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : "Unknown error";
			this._errorMsg = errorMessage;
			throw error;
		}
	}
	/**
	* Called from wizard to reload form data from storage.
	* Override in implementing class to populate form fields.
	*
	* @param _reloadedOrder - The order data to reload
	*/
	reloadFromOrder(_reloadedOrder) {}
	/**
	* Called when this step becomes active (navigated to via Next, Previous, or Go-To).
	* Override in implementing class to react to step activation, e.g. to
	* pre-fill fields based on data collected in previous steps.
	*
	* @param _orderFormData - The current accumulated wizard form data
	*/
	onStepEnter(_orderFormData) {}
	/**
	* Get the step title. Override to customize.
	*/
	_getTitle() {
		return this.stepTitle;
	}
	/**
	* Additional validation beyond form field validation.
	* Override to add custom validation logic.
	* @returns true if form data is valid
	*/
	_isValidFormData() {
		return true;
	}
	/**
	* Prepare the order after step update (for side-effects).
	* Used e.g. to reset values based on other selections.
	*
	* @param updatedOrder - Order with updates from this step
	* @returns Prepared order with any additional changes
	*/
	_prepareOrderAfterStepUpdate(updatedOrder) {
		return updatedOrder;
	}
	/**
	* Extract data from form inputs.
	* Override to customize data extraction.
	*
	* @returns Prepared data object from form
	*/
	_prepareDataFromForm() {
		if (!this._formElement?.valid) throw new Error("Invalid form, prepare not possible");
		return this._formElement?.getValues().parsed;
	}
	/**
	* Called on input change to trigger auto-save.
	* Debounced to avoid excessive events.
	*/
	_doInputChange() {
		if (this._inputChangeTimeoutRef) window.clearTimeout(this._inputChangeTimeoutRef);
		this._inputChangeTimeoutRef = window.setTimeout(() => {
			this.dispatchEvent(new CustomEvent("wizard-step-update", {
				detail: this._prepareDataFromForm(),
				bubbles: true,
				composed: true
			}));
		}, DELAY_TIME);
	}
};
__decorate([property({ type: String })], PdDefaultWizardStep.prototype, "stepTitle", void 0);
__decorate([property({ type: String })], PdDefaultWizardStep.prototype, "stepDescription", void 0);
__decorate([property({ type: Boolean })], PdDefaultWizardStep.prototype, "withRequiredInfo", void 0);
__decorate([property({ type: Boolean })], PdDefaultWizardStep.prototype, "withAutoTrim", void 0);
__decorate([state()], PdDefaultWizardStep.prototype, "_errorMsg", void 0);
__decorate([state()], PdDefaultWizardStep.prototype, "_validForm", void 0);
__decorate([query("#stepFormContainerId")], PdDefaultWizardStep.prototype, "_formElement", void 0);
//#endregion
export { PdDefaultWizardStep };
